import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import TuneRoundedIcon from "@material-ui/icons/TuneRounded";

import ChatBubbleOutlineRoundedIcon from "@material-ui/icons/ChatBubbleOutlineRounded";
import FolderOpenRoundedIcon from "@material-ui/icons/FolderOpenRounded";
import HourglassEmptyRoundedIcon from "@material-ui/icons/HourglassEmptyRounded";
import AssignmentIndRoundedIcon from "@material-ui/icons/AssignmentIndRounded";
import CheckCircleOutlineRoundedIcon from "@material-ui/icons/CheckCircleOutlineRounded";
import PersonAddRoundedIcon from "@material-ui/icons/PersonAddRounded";
import DateRangeRoundedIcon from "@material-ui/icons/DateRangeRounded";
import ArrowDropDownRoundedIcon from "@material-ui/icons/ArrowDropDownRounded";

import useDashboard from "../../hooks/useDashboard";
import ConversationStartsModal from "../../components/ConversationStartsModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ChannelIcon from "../../components/ChannelIcon";

// Convenience: every dashboard string passes through this so missing
// keys fall back gracefully to the Hebrew default. Other languages
// pick up the right copy as soon as their bundle includes the key.
const T = (key, fallback) => i18n.t(`dashboard.${key}`, { defaultValue: fallback });

// Premium dashboard layout — visuals only, mirroring the mockup Raviv
// shipped 2026-05-08. Real data is plugged where the existing
// `useDashboard` endpoint already exposes it; the rest is illustrative
// sample data matched to the mock so we can iterate on the *look*
// before wiring backend metrics.

// Premium fintech-minimal redesign (Greyola-style reference) —
// the previous brand-gradient hero language has been thrown out
// entirely. New visual rules:
//   * page is a very light cool-gray, not a green gradient
//   * cards are pure white, 24px radius, soft 2-layer shadow
//   * primary numbers are HUGE (~48px) and pure black, not green
//   * green is reserved for tiny status chips + a single
//     highlighted chart element — never as a card-wide fill
//   * tabs / buttons that need active state use a dark (#0B0D0C)
//     pill, not a gradient
//   * hover tooltips are dark pills with a white label

// Mobile design system tokens — VERBATIM from spec sections A.2–A.6.
// Shared across all surfaces; first-mover defines them here so the
// rest of the dashboard's breakpoint rules can reference them. Do
// not introduce new colors / spacings outside these scales.
const SP = {
  xxs: 4,
  xs:  8,
  sm:  12,
  md:  16,
  lg:  20,
  xl:  24,
  xxl: 32,
  hero: 48,
};
const TT = {
  min:    44,
  primary: 48,
  hero:   56,
};
const TYPE = {
  caption:     { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:        { fontSize: 14, lineHeight: 1.5 },
  bodyStrong:  { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading:  { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:     { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
  display:     { fontSize: 24, lineHeight: 1.25, fontWeight: 700 },
};
const RAD = {
  pill: 999,
  sm:   8,
  md:   12,
  lg:   16,
  xl:   20,
};
const COL = {
  brandGreen:     '#25d366',
  brandGreenDeep: '#128c7e',
  brandGreenSoft: 'rgba(37,211,102,0.08)',
  textPrimary:    '#0B0D0C',
  textSecondary:  '#52525b',
  borderSoft:     '#eaeaea',
  pageBg:         '#f7faf8',
  cardBg:         '#ffffff',
  destructive:    '#ef4444',
  warning:        '#f59e0b',
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    // Premium dark recipe: near-black base + dual radial brand
    // glows. Layers stay close in lightness (bg #0B0D0C → cards
    // #131614 → popovers #1A1E1B) — hierarchy carried by 1px
    // hairline borders + inset top highlights, NOT by big contrast
    // jumps. Light theme keeps the airy gray.
    page: {
      // Anti-pattern E removed: `minHeight: 100vh` breaks iOS toolbar
      // shrinking on the page root. Let the content's natural height
      // drive scrolling. The page-bg color still fills via the parent
      // MainContainer.
      padding: "32px 36px 64px",
      direction: theme.direction,
      position: "relative",
      background: isDark ? "#0B0D0C" : "#f5f5f7",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: isDark
          ? `radial-gradient(900px circle at 15% -10%, rgba(46, 227, 122, 0.09), transparent 55%),
             radial-gradient(700px circle at 90% 110%, rgba(18, 140, 126, 0.07), transparent 50%)`
          : "transparent",
      },
      "& > *": { position: "relative" },
      [theme.breakpoints.down("sm")]: { padding: "18px 14px 48px" },
      // Mobile-only override per spec A.2: page horizontal padding on
      // mobile = SP.sm (12). Vertical kept compact.
      [theme.breakpoints.down("xs")]: {
        padding: `${SP.md}px ${SP.sm}px ${SP.xxl}px`,
      },
    },
    breadcrumb: {
      fontSize: 12.5,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
      marginBottom: 14,
      "& > span:not(:last-child)::after": {
        content: '"→"',
        margin: "0 8px",
        opacity: 0.5,
      },
    },
    headerRow: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: 16,
      marginBottom: 28,
      flexWrap: "wrap",
      // On phones the greeting block + dateGroup compete for the same
      // 360 px row and end up wrapping unpredictably (each pill claims
      // its own line). Stack the two blocks vertically and let the
      // dateGroup span the full width so the period selector +
      // filters wrap cleanly under the greeting.
      // Spec C.1 + A.2: column-stack + alignItems stretch + gap SP.md (16).
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: SP.md,
        marginBottom: SP.lg,
      },
    },
    greeting: {
      fontSize: 38,
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: -1,
      color: isDark ? "#fafafa" : "#0B0D0C",
      "& span": {
        display: "block",
        marginTop: 8,
        fontSize: 14,
        fontWeight: 500,
        color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
        letterSpacing: 0,
      },
      [theme.breakpoints.down("sm")]: { fontSize: 28 },
    },
    livePulse: {
      marginTop: 16,
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },
    pulseChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 14px",
      borderRadius: 999,
      background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #eaeaea",
      fontSize: 12.5,
      fontWeight: 600,
      color: isDark ? "#d4d4d8" : "#3f3f46",
      "& strong": {
        fontSize: 13,
        fontWeight: 800,
        color: isDark ? "#fafafa" : "#0B0D0C",
      },
      "& .lbl": {
        color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
        fontWeight: 500,
      },
    },
    pulseDotGreen: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      background: "#22c55e",
      boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.18)",
    },
    liveChip: {
      backgroundColor: "#dcfce7 !important",
      color: "#16a34a !important",
      fontWeight: "700 !important",
      fontSize: "0.8rem !important",
      animation: "$livePulse 2s ease-in-out infinite",
    },
    "@keyframes livePulse": {
      "0%":   { opacity: 1 },
      "50%":  { opacity: 0.55 },
      "100%": { opacity: 1 },
    },
    dateGroup: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      // Spec C.1: dateGroup xs full-width, button stack vertical so
      // every pill becomes a full-width tappable row instead of fighting
      // for inline space. Each child stretches; pills below set their
      // own min-height to satisfy TT.min (44).
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        flexDirection: "column",
        alignItems: "stretch",
        gap: SP.xs,
        "& > *": { width: "100%" },
      },
    },
    datePill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "11px 16px",
      borderRadius: 14,
      background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #eaeaea",
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#d4d4d8" : "#0B0D0C",
      cursor: "pointer",
      transition: "border-color .15s ease, transform .12s ease",
      "& svg": {
        fontSize: 18,
        color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
      },
      "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.20)" : "#a1a1aa" },
      "&:active": { transform: "scale(0.98)" },
      // Spec A.3: every interactive element on mobile ≥44px.
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
        justifyContent: "center",
      },
    },
    dropdownPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "11px 14px",
      borderRadius: 14,
      background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #eaeaea",
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#d4d4d8" : "#0B0D0C",
      cursor: "pointer",
      "& svg": { fontSize: 18, opacity: 0.65 },
      // Spec A.3: ≥44px tap target on mobile.
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
        justifyContent: "center",
      },
    },
    // KPI card — anatomy of the reference's "Total Portfolio Value"
    // tile. Massive number is the protagonist; everything else is
    // supportive.
    // Linear / Vercel signature card recipe for dark mode:
    //   * surface color sits ~4-6 lightness points above page bg
    //   * 1px subtle hairline border, not a big colored ring
    //   * inset 0 1px 0 rgba(255,255,255,0.04) on top edge —
    //     simulates a light catching the bevel, the single most
    //     "premium dark" trick. Without this, dark cards look
    //     flat and cheap. With it, they look like glass.
    //   * NO drop shadow at rest; hover gets a soft, bigger one
    //   * border-color tightens on hover instead of solid color
    kpiCard: {
      position: "relative",
      padding: "22px 22px 18px",
      borderRadius: 18,
      background: isDark ? "#131614" : "#fff",
      border: isDark
        ? "1px solid rgba(255,255,255,0.07)"
        : "1px solid #eaeaea",
      boxShadow: isDark
        ? "inset 0 1px 0 0 rgba(255,255,255,0.04)"
        : "0 1px 2px rgba(15, 15, 15, 0.04), 0 6px 16px rgba(15, 15, 15, 0.04)",
      transition: "border-color .18s ease, box-shadow .18s ease",
      "&:hover": {
        borderColor: isDark
          ? "rgba(255,255,255,0.14)"
          : "#d4d4d8",
        boxShadow: isDark
          ? `inset 0 1px 0 0 rgba(255,255,255,0.06),
             0 1px 2px rgba(0, 0, 0, 0.4),
             0 8px 24px -8px rgba(0, 0, 0, 0.55)`
          : "0 1px 2px rgba(15, 15, 15, 0.04), 0 10px 30px rgba(15, 15, 15, 0.08)",
      },
      // Phone: tighter padding so the 36 px number + trend pill +
      // sparkline don't bust the card height that the 6-col grid hands
      // us at xs. Pairs with the kpiNumber xs override below.
      // Spec C.1: mobile padding = SP.md (16) all sides.
      [theme.breakpoints.down("xs")]: {
        padding: SP.md,
        borderRadius: RAD.md,
      },
    },
    kpiTopRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 6,
    },
    kpiLabel: {
      fontSize: 13,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
      letterSpacing: 0,
      textTransform: "none",
    },
    // Theme-aware subtitle for ChannelKpiCard — same token as kpiLabel so the
    // "X נסגרו · Y% המרה" line stays legible on the dark #131614 card.
    channelKpiSubtitle: {
      fontSize: 12,
      marginTop: 3,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
    },
    // Theme-aware "מקורות שיחה" section eyebrow.
    channelSectionTitle: {
      fontSize: 12,
      fontWeight: 700,
      color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },
    // First-load spinner overlay — covers the zero/empty state until the
    // initial /dashboard fetch resolves. Page is position:relative.
    loadingOverlay: {
      position: "absolute",
      inset: 0,
      zIndex: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "#0B0D0C" : "#f5f5f7",
    },
    kpiIconBox: {
      width: 34,
      height: 34,
      borderRadius: 12,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(255,255,255,0.04)" : "#f4f4f5",
      color: isDark ? "rgba(255,255,255,0.65)" : "#0B0D0C",
      "& svg": { fontSize: 18 },
    },
    kpiNumber: {
      fontSize: 36,
      fontWeight: 800,
      color: isDark ? "#fafafa" : "#0B0D0C",
      lineHeight: 1,
      letterSpacing: -1,
      marginTop: 4,
      fontFeatureSettings: "'tnum' 1",
      // 36 px is too tall for the 6-col phone grid — shrinks the card's
      // internal padding to nothing and clips the trend chip below.
      // Spec C.1: kpiNumber xs fontSize 24 (matches TYPE.display size).
      [theme.breakpoints.down("xs")]: {
        fontSize: 24,
        letterSpacing: -0.5,
      },
    },
    kpiTrendRow: {
      marginTop: 10,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 8px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 700,
      "&::before": {
        content: '""',
        width: 5,
        height: 5,
        borderRadius: "50%",
      },
      // Spec C.1: trend pill below number not beside. Force a flex line
      // break by going display:flex with align-self start, so when this
      // pill is in a flex/column kpi card it doesn't try to share a row
      // with the sparkline footer or the number above.
      [theme.breakpoints.down("xs")]: {
        marginTop: SP.xs,
        alignSelf: "flex-start",
      },
    },
    trendUp: {
      background: isDark ? "rgba(34, 197, 94, 0.15)" : "#ecfdf5",
      color: "#15803d",
      "&::before": { background: "#22c55e" },
    },
    trendDown: {
      background: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
      color: "#b91c1c",
      "&::before": { background: "#ef4444" },
    },
    trendCaption: {
      color: isDark ? "rgba(255,255,255,0.45)" : "#a1a1aa",
      fontWeight: 500,
      marginInlineStart: 4,
    },
    sectionGrid: { marginTop: 6 },
    panel: {
      padding: 26,
      borderRadius: 18,
      background: isDark ? "#131614" : "#fff",
      border: isDark
        ? "1px solid rgba(255,255,255,0.07)"
        : "1px solid #eaeaea",
      boxShadow: isDark
        ? "inset 0 1px 0 0 rgba(255,255,255,0.04)"
        : "0 1px 2px rgba(15, 15, 15, 0.04), 0 6px 16px rgba(15, 15, 15, 0.04)",
      height: "100%",
      // 26 px panel padding on a 360 px viewport leaves a ~280 px
      // content box — too tight for the sections / agents tables.
      // Spec A.2: mobile card padding = SP.md (16). Spec A.5: card
      // border-radius mobile = RAD.md (12).
      [theme.breakpoints.down("xs")]: {
        padding: SP.md,
        borderRadius: RAD.md,
      },
    },
    // Horizontal scroll shell for the wide sections / agents tables
    // on phones. Same pattern as CompaniesManager. -webkit-overflow
    // -scrolling:touch ensures momentum scrolling on iOS Safari.
    // Spec B.1 tableScroll: bleed to screen edges with negative
    // margins on mobile + min-width:max-content on the inner table +
    // nowrap on cells so columns stay readable while horizontal scroll
    // is the user's only escape hatch.
    tableScroll: {
      width: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      [theme.breakpoints.down("xs")]: {
        margin: `0 -${SP.sm}px`,
        padding: `0 ${SP.sm}px`,
        "& table": { minWidth: "max-content" },
        "& th, & td": { whiteSpace: "nowrap" },
      },
    },
    panelHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 18,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: isDark ? "#fafafa" : "#0B0D0C",
      letterSpacing: -0.3,
    },
    panelSub: {
      fontSize: 12,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
      marginTop: 2,
    },
    chartWrap: {
      width: "100%",
      height: 260,
      // Bleed the chart to the panel edges on mobile so the x-axis ticks
      // get the full card width to breathe. Panel padding is SP.md (16) on
      // xs — negative margins reclaim those 32px (~10% more chart width on
      // 320px viewport). Y-axis ticks remain inside via Recharts margin prop.
      [theme.breakpoints.down("xs")]: {
        marginInlineStart: -SP.md,
        marginInlineEnd: -SP.md,
        width: `calc(100% + ${SP.md * 2}px)`,
      },
    },
    donutWrap: {
      display: "flex",
      alignItems: "center",
      gap: 24,
      [theme.breakpoints.down("xs")]: { flexDirection: "column" },
    },
    donutCenter: {
      position: "relative",
      width: 220,
      height: 220,
      flexShrink: 0,
    },
    donutCenterText: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      textAlign: "center",
      "& .num": {
        display: "block",
        fontSize: 38,
        fontWeight: 800,
        color: isDark ? "#fafafa" : "#0B0D0C",
        lineHeight: 1,
        letterSpacing: -1,
      },
      "& .lbl": {
        display: "block",
        fontSize: 11,
        fontWeight: 500,
        color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
        marginTop: 6,
      },
    },
    donutLegend: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    legendRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      fontSize: 13,
      fontWeight: 500,
      color: isDark ? "#d4d4d8" : "#3f3f46",
    },
    legendDotName: { display: "inline-flex", alignItems: "center", gap: 10 },
    legendDot: { width: 8, height: 8, borderRadius: "50%" },
    legendValue: {
      fontWeight: 700,
      color: isDark ? "#fafafa" : "#0B0D0C",
      fontFeatureSettings: "'tnum' 1",
    },
    metricGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 1,
      background: isDark ? "rgba(255,255,255,0.04)" : "#eaeaea",
      borderRadius: 16,
      overflow: "hidden",
      border: isDark
        ? "1px solid rgba(255,255,255,0.04)"
        : "1px solid #eaeaea",
    },
    metricCard: {
      padding: "18px 18px 16px",
      background: isDark ? "#171717" : "#fff",
    },
    metricLabel: {
      fontSize: 12,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
      marginBottom: 8,
    },
    metricValue: {
      fontSize: 26,
      fontWeight: 800,
      color: isDark ? "#fafafa" : "#0B0D0C",
      lineHeight: 1,
      letterSpacing: -0.5,
      fontFeatureSettings: "'tnum' 1",
    },
    metricDelta: { fontSize: 11, fontWeight: 700, marginTop: 6 },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      fontSize: 13,
    },
    th: {
      padding: "10px 16px",
      textAlign: theme.direction === "rtl" ? "right" : "left",
      color: isDark ? "rgba(255,255,255,0.55)" : "#a1a1aa",
      fontWeight: 600,
      fontSize: 11.5,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid #eaeaea",
    },
    td: {
      padding: "10px 16px",
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.04)"
        : "1px solid #f4f4f5",
      color: isDark ? "#e4e4e7" : "#0B0D0C",
      fontWeight: 500,
    },
    statusChip: {
      height: 24,
      fontWeight: 600,
      fontSize: 11.5,
      borderRadius: 8,
    },
    statusOnline: {
      background: "#ecfdf5",
      color: "#15803d",
    },
    statusOffline: {
      background: "#fee2e2",
      color: "#b91c1c",
    },
    roleBadge: {
      // Sits inline inside the .greeting div which runs fontSize: 38 +
      // lineHeight: 1.1. Without an explicit display+lineHeight reset
      // the Chip inherits the 41.8 px line box and the label glyphs get
      // hung from the parent baseline → text drifts to the top of the
      // green pill. Lock the chip to its own flex line-box so the label
      // is genuinely vertically centered inside the 22 px pill.
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      height: 22,
      lineHeight: 1,
      fontSize: 11,
      fontWeight: 700,
      fontFamily: "Roboto, sans-serif",
      borderRadius: 6,
      marginInlineStart: 10,
      verticalAlign: "middle",
      color: "#fff",
      "& .MuiChip-label": {
        color: "#fff !important",
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 1,
        paddingTop: 0,
        paddingBottom: 0,
        fontFamily: "Roboto, sans-serif",
      },
    },
    agentAvatar: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: "#128c7e",
      color: "#fff",
      fontSize: 12,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginInlineEnd: 8,
      flexShrink: 0,
      textTransform: "uppercase",
    },
    agentNameCell: {
      display: "flex",
      alignItems: "center",
    },
    totalRow: {
      "& td": {
        borderTop: isDark
          ? "2px solid rgba(255,255,255,0.10)"
          : "2px solid #e4e4e7",
        borderBottom: "none",
        fontWeight: 800,
        color: isDark ? "#fafafa" : "#0B0D0C",
      },
    },
    filterPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "11px 14px",
      borderRadius: 14,
      background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #eaeaea",
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#d4d4d8" : "#0B0D0C",
      cursor: "pointer",
      "& svg": { fontSize: 18, opacity: 0.65 },
      // Spec A.3: ≥44px tap target on mobile.
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
        justifyContent: "center",
      },
    },
    filterPillActive: {
      borderColor: "#128c7e",
      color: "#128c7e",
      background: isDark ? "rgba(18,140,126,0.12)" : "#f0fdf9",
    },
    filterPopover: {
      padding: "20px 20px 16px",
      minWidth: 280,
      display: "flex",
      flexDirection: "column",
      gap: 16,
      // Spec C.1: filters popover xs full-width minus screen-edge gutter.
      [theme.breakpoints.down("xs")]: {
        minWidth: 0,
        width: "100%",
        padding: SP.md,
        gap: SP.sm,
      },
    },
    // Spec C.1: PaperProps width forced to calc(100vw - 24px) on mobile
    // so the popover spans the viewport edge-to-edge (minus SP.sm gutter
    // either side) and never clips off-screen when anchored on a small
    // viewport.
    filterPopoverPaper: {
      [theme.breakpoints.down("xs")]: {
        width: "calc(100vw - 24px)",
        maxWidth: "calc(100vw - 24px)",
        left: `${SP.sm}px !important`,
        right: `${SP.sm}px !important`,
      },
    },
    filterPopoverTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: isDark ? "#fafafa" : "#0B0D0C",
      marginBottom: 4,
    },
    customDateRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      marginTop: 4,
    },
    sectionSwatch: {
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: 3,
      marginInlineEnd: 10,
      verticalAlign: "middle",
      border: isDark
        ? "1px solid rgba(255,255,255,0.18)"
        : "1px solid rgba(0,0,0,0.08)",
    },
    sectionName: {
      display: "inline-flex",
      alignItems: "center",
      fontWeight: 700,
      color: isDark ? "#fafafa" : "#0B0D0C",
    },
    sectionStatusBadge: {
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 700,
      fontFeatureSettings: "'tnum' 1",
      letterSpacing: 0.1,
    },
    badgeOpen: {
      background: isDark ? "rgba(34, 197, 94, 0.16)" : "#ecfdf5",
      color: "#15803d",
    },
    badgePending: {
      background: isDark ? "rgba(212, 212, 216, 0.12)" : "#f4f4f5",
      color: isDark ? "#e4e4e7" : "#52525b",
    },
    badgeClosed: {
      background: isDark ? "rgba(255,255,255,0.06)" : "#0B0D0C",
      color: isDark ? "#fafafa" : "#fafafa",
    },
    sectionTotal: {
      fontWeight: 800,
      fontFeatureSettings: "'tnum' 1",
      color: isDark ? "#fafafa" : "#0B0D0C",
    },
  };
});

const PALETTE = {
  green: { bg: "#dcfce7", fg: "#16a34a" },
  orange: { bg: "#ffedd5", fg: "#1ebe5d" },
  purple: { bg: "#dcfce7", fg: "#075e54" },
  pink: { bg: "#fce7f3", fg: "#db2777" },
  red: { bg: "#fee2e2", fg: "#dc2626" },
  cyan: { bg: "#cffafe", fg: "#0891b2" },
  indigo: { bg: "#e0e7ff", fg: "#1ebe5d" },
  amber: { bg: "#fef3c7", fg: "#d97706" },
};

const Sparkline = ({ data, stroke }) => {
  // Always reserve the same 40px footer (32 height + 8 top margin)
  // so KPI cards line up at identical heights even when a card has
  // no series to draw — premium dashboards rely on consistent grid
  // rhythm; mismatched card heights are the give-away that the
  // grid is inconsistent.
  const hasData = Array.isArray(data) && data.length >= 2;
  return (
    <div style={{ height: 32, marginTop: 8, marginLeft: -4, marginRight: -4 }}>
      {hasData && (
        <ResponsiveContainer>
          <LineChart data={data}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={stroke}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// Memoized: KpiCard is re-mounted 6x on every render, including the
// throttled 5s live-socket tick that only touches `counters`/`attendants`.
// React.memo lets it bail out whenever the `kpis` array it's fed (now
// itself memoized below) hasn't changed identity for a given card.
const KpiCard = React.memo(({ classes, label, value, icon: Icon, spark, trend }) => (
  <div className={classes.kpiCard}>
    <div className={classes.kpiTopRow}>
      <span className={classes.kpiLabel}>{label}</span>
      {Icon && (
        <span className={classes.kpiIconBox}>
          <Icon />
        </span>
      )}
    </div>
    <div className={classes.kpiNumber}>{value}</div>
    {trend && trend.dir !== "flat" && (
      <div className={`${classes.kpiTrendRow} ${trend.dir === "up" ? classes.trendUp : classes.trendDown}`}>
        {trend.dir === "up" ? "↑" : "↓"} {trend.pct}%
        <span className={classes.trendCaption}>
          {T("trend.vsPrev", "מהתקופה הקודמת")}
        </span>
      </div>
    )}
    <Sparkline data={spark} stroke="#22c55e" />
  </div>
));

// Channel-specific brand colours and labels used by ChannelKpiCard.
// All five channels are always rendered — cards show 0/0% when no data
// yet exists for that channel so the row stays visually consistent.
const CHANNEL_META = {
  whatsapp:  { label: "WhatsApp",  brand: "#25d366" },
  facebook:  { label: "Messenger", brand: "#0062E0" },
  instagram: { label: "Instagram", brand: "#d6249f" },
  telegram:  { label: "Telegram",  brand: "#29B6F6" },
  email:     { label: "Email",     brand: "#5C6BC0" },
};

/**
 * ChannelKpiCard — mirrors KpiCard but shows channel-source analytics.
 *
 * Displays per-channel totals, closed conversations (=conversions),
 * conversion rate, and a trend arrow vs the previous period.
 * Uses the same CSS classes as KpiCard to guarantee identical
 * spacing, radius, shadows, and typography.
 */
const ChannelKpiCard = React.memo(({ classes, channel, total, closed, prevTotal, trend }) => {
  const meta = CHANNEL_META[channel] || { label: channel, brand: "#a1a1aa" };
  const convRate = total > 0 ? Math.round((closed / total) * 100) : 0;
  return (
    <div
      className={classes.kpiCard}
      style={{ borderTop: `3px solid ${meta.brand}` }}
    >
      <div className={classes.kpiTopRow}>
        <span className={classes.kpiLabel}>{meta.label}</span>
        <span className={classes.kpiIconBox}>
          <ChannelIcon channel={channel} size={18} />
        </span>
      </div>
      <div className={classes.kpiNumber}>{(total || 0).toLocaleString()}</div>
      <div className={classes.channelKpiSubtitle}>
        {(closed || 0).toLocaleString()} {T("channel.closed", "נסגרו")}
        {" · "}
        <span style={{ color: meta.brand, fontWeight: 600 }}>{convRate}%</span>
        {" "}{T("channel.conv", "המרה")}
      </div>
      {trend && trend.dir !== "flat" && (
        <div className={`${classes.kpiTrendRow} ${trend.dir === "up" ? classes.trendUp : classes.trendDown}`}>
          {trend.dir === "up" ? "↑" : "↓"} {trend.pct}%
          <span className={classes.trendCaption}>
            {T("trend.vsPrev", "מהתקופה הקודמת")}
          </span>
        </div>
      )}
    </div>
  );
});

// Build a 24-bucket hourly histogram from items with a `createdAt`.
// Used for both messages-by-hour and tickets-by-hour. Caller filters
// the source array to "today" before passing in.
const bucketByHour = (items) => {
  const buckets = Array.from({ length: 24 }, (_, h) => ({
    label: `${String(h).padStart(2, "0")}:00`,
    value: 0,
  }));
  if (!Array.isArray(items)) return buckets;
  for (const it of items) {
    const ts = it?.createdAt;
    if (!ts) continue;
    const d = new Date(ts);
    if (isNaN(d.getTime())) continue;
    buckets[d.getHours()].value += 1;
  }
  return buckets;
};

const isToday = (ts) => {
  if (!ts) return false;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
};

const isYesterday = (ts) => {
  if (!ts) return false;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return false;
  const y = new Date();
  y.setDate(y.getDate() - 1);
  return (
    d.getDate() === y.getDate() &&
    d.getMonth() === y.getMonth() &&
    d.getFullYear() === y.getFullYear()
  );
};

const isWithinLastDays = (ts, days) => {
  if (!ts) return false;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  cutoff.setHours(0, 0, 0, 0);
  return d.getTime() >= cutoff.getTime();
};

// Single predicate for the chart range pills — returns the appropriate
// filter for "today" / "yesterday" / "7d" / "30d". Charts call this
// once and the dropdown switches just swap which filter is in use.
const chartRangeFilter = (range) => {
  if (range === "today") return isToday;
  if (range === "yesterday") return isYesterday;
  if (range === "7d") return (t) => isWithinLastDays(t, 7);
  if (range === "30d") return (t) => isWithinLastDays(t, 30);
  return isToday;
};

const formatDuration = (sec) => {
  if (sec == null || isNaN(sec)) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Returns { pct: number, dir: "up"|"down"|"flat" } or null if no comparison data.
const calcTrend = (current, previous) => {
  const cur = Number(current) || 0;
  const prev = Number(previous);
  if (!prev && prev !== 0) return null; // previous is null/undefined
  if (prev === 0) return cur > 0 ? { pct: 100, dir: "up" } : { pct: 0, dir: "flat" };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { pct: Math.abs(pct), dir: pct > 0 ? "up" : pct < 0 ? "down" : "flat" };
};

const formatRange = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  const fmt = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  return `${fmt(start)} - ${fmt(end)}`;
};

// Range options are built lazily so each render picks up the active
// language. The labels go through T() with sensible Hebrew defaults.
const useRangeOptions = () => useMemo(() => [
  { value: 1, label: T("range.today", "היום") },
  { value: 7, label: T("range.7d", "7 ימים") },
  { value: 30, label: T("range.30d", "30 ימים") },
  { value: 90, label: T("range.90d", "90 ימים") },
// eslint-disable-next-line react-hooks/exhaustive-deps
], [i18n.language]);

// Time-of-day aware greeting — premium SaaS pattern (Linear, Notion,
// Stripe all do it). Falls through i18n so other languages get their
// own morning/afternoon/evening/night variants when the bundle has them.
const greetingForHour = (hour) => {
  if (hour >= 5 && hour < 12) return T("greet.morning", "בוקר טוב");
  if (hour >= 12 && hour < 17) return T("greet.afternoon", "צהריים טובים");
  if (hour >= 17 && hour < 22) return T("greet.evening", "ערב טוב");
  return T("greet.night", "לילה טוב");
};

// Bucket items into N day buckets stretching back from today.
// Used to feed the small sparklines under each KPI card so the card
// shows trend-at-a-glance, not just a static number.
const bucketByDay = (items, days = 7) => {
  const buckets = Array.from({ length: days }, () => 0);
  if (!Array.isArray(items)) return buckets.map((value, i) => ({ i, value }));
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (const it of items) {
    const ts = it?.createdAt;
    if (!ts) continue;
    const d = new Date(ts);
    if (isNaN(d.getTime())) continue;
    // Normalize the item to its own LOCAL midnight before diffing. Diffing a
    // timestamp-with-time against local-midnight-today floored negative for
    // anything later than 00:00 today (dropping all of today's items) and
    // put yesterday's items in today's bucket. Midnight-to-midnight + round
    // gives exact whole days and is DST-safe.
    const dMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startOfDay - dMidnight) / (24 * 60 * 60 * 1000));
    if (diffDays < 0 || diffDays >= days) continue;
    const idx = days - 1 - diffDays;
    buckets[idx] += 1;
  }
  return buckets.map((value, i) => ({ i, value }));
};

// Switches between hourly (day=1) and daily buckets (day>1).
// When day>1 each bucket gets a DD/MM label so the chart XAxis is readable.
const bucketMessagesByPeriod = (items, days) => {
  if (days === 1) return bucketByHour(items);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const buckets = Array.from({ length: days }, (_, i) => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() - (days - 1 - i));
    const label = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { label, value: 0 };
  });
  if (!Array.isArray(items)) return buckets;
  for (const it of items) {
    const ts = it?.createdAt;
    if (!ts) continue;
    const d = new Date(ts);
    if (isNaN(d.getTime())) continue;
    // See bucketByDay above: normalize to local midnight so today's items
    // aren't floored to -1 (dropped) and yesterday's don't shift into today.
    const dMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startOfToday - dMidnight) / (24 * 60 * 60 * 1000));
    if (diffDays < 0 || diffDays >= days) continue;
    const idx = days - 1 - diffDays;
    buckets[idx].value += 1;
  }
  return buckets;
};

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { find } = useDashboard();
  // Throttle: prevent a burst of ticket events from firing a /dashboard
  // refetch on every individual message. 5s window keeps KPI cards fresh
  // within one business interaction cycle without hammering the backend.
  const liveRefreshThrottleRef = useRef(0);
  const [counters, setCounters] = useState({});
  const [prevCounters, setPrevCounters] = useState(null);
  // First-load / refetch flag — drives the spinner overlay so the page
  // doesn't flash all-zero KPIs + empty charts before the initial fetch lands.
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [sections, setSections] = useState([]);
  // Channel analytics — populated from /dashboard?channelStats
  const [channelStats, setChannelStats] = useState({});
  const [prevChannelStats, setPrevChannelStats] = useState({});

  // ⚠️ DO NOT CHANGE THIS DEFAULT — intentionally set to 1 (today) per Raviv 31/05/2026.
  // User opens dashboard and sees TODAY's data. Can switch to 7/30d manually via the period selector.
  const [periodDays, setPeriodDays] = useState(1);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [rangeMenuAnchor, setRangeMenuAnchor] = useState(null);
  // Per-chart range — separate state for the two trend panels (Messages
  // by hour / Tickets by hour). Default "today" matches what the
  // panels were always showing; switching to "yesterday" / 7d / 30d
  // now actually filters the underlying data.
  const [chartRange, setChartRange] = useState("today");
  const [chartMenuAnchor, setChartMenuAnchor] = useState(null);

  // Conversation Starts modal — Raviv 2026-06-08. State holds the row data
  // for the currently-opened modal, or null when closed.
  // Shape: { name, total, newTickets, reactivations }
  const [csModal, setCsModal] = useState(null);

  // Entity filter state
  const [filterUserId, setFilterUserId] = useState("");
  const [filterQueueId, setFilterQueueId] = useState("");
  const [filterWhatsappId, setFilterWhatsappId] = useState("");
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterQueues, setFilterQueues] = useState([]);
  const [filterWhatsapps, setFilterWhatsapps] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);

  // Fetch filter lists once on mount
  useEffect(() => {
    api.get("/queue").then(({ data }) => setFilterQueues(data || [])).catch(() => {});
    api.get("/whatsapp/?session=0").then(({ data }) => setFilterWhatsapps(data || [])).catch(() => {});
    if (user?.profile === "admin" || user?.profile === "supervisor") {
      api.get("/users", { params: { pageNumber: 1 } })
        .then(({ data }) => setFilterUsers(data?.users || []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;

    // In custom mode, skip the fetch until both dates are provided
    if (periodDays === "custom" && (!customFrom || !customTo)) return;

    const params = {};
    if (periodDays === "custom") {
      if (customFrom) params.date_from = customFrom;
      if (customTo)   params.date_to   = customTo;
    } else if (periodDays === 1) {
      // "Today" = from last 08:00 Israel time (UTC+3) to now.
      // This ensures the counter resets every morning at 08:00 regardless
      // of when the user opens the dashboard — not a rolling 24-hour window.
      // If it's currently before 08:00 Israel time, the window shows data
      // from yesterday's 08:00 (i.e., the most-recent 08:00 reset).
      const now = new Date();
      // Express "now" in Israel time (UTC+3 summer / IDT)
      const israelNow = new Date(now.getTime() + 3 * 3600 * 1000);
      const yy = israelNow.getUTCFullYear();
      const mm = String(israelNow.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(israelNow.getUTCDate()).padStart(2, "0");
      // Today's 08:00 Israel = 05:00 UTC (UTC offset +3)
      const reset = new Date(`${yy}-${mm}-${dd}T05:00:00.000Z`);
      // If we are still before today's 08:00 Israel, use yesterday's 08:00
      if (now < reset) reset.setUTCDate(reset.getUTCDate() - 1);
      params.date_from = reset.toISOString();
      params.date_to   = now.toISOString();
    } else {
      params.days = periodDays;
    }
    if (filterUserId)    params.userId    = filterUserId;
    if (filterQueueId)   params.queueId   = filterQueueId;
    if (filterWhatsappId) params.whatsappId = filterWhatsappId;

    // NOTE: no setIsLoading(true) here — isLoading starts true and the
    // overlay is first-load only. Filter-change refetches keep the previous
    // KPIs visible instead of blanking the page (and its filter controls).
    find(params).then((data) => {
      if (!mounted) return;
      const root = Array.isArray(data) ? data[0] : data;
      if (!root) return;
      setCounters(root.counters || {});
      setPrevCounters(root.prevCounters || null);
      setMessages(Array.isArray(root.messages) ? root.messages : []);
      setTickets(Array.isArray(root.tickets) ? root.tickets : []);
      setAttendants(Array.isArray(root.attendants) ? root.attendants : []);
      setSections(Array.isArray(root.sections) ? root.sections : []);

      // Normalize channel-stats arrays (from SQL json_agg) into objects keyed by channel name.
      // SQL COUNT returns numeric strings in JSON — cast to Number.
      const rawCh = Array.isArray(root.channelStats) ? root.channelStats : [];
      const ch = {};
      for (const row of rawCh) {
        if (row?.channel) ch[row.channel] = { total: Number(row.total) || 0, closed: Number(row.closed) || 0 };
      }
      setChannelStats(ch);

      const rawPrev = Array.isArray(root.prevChannelStats) ? root.prevChannelStats : [];
      const pch = {};
      for (const row of rawPrev) {
        if (row?.channel) pch[row.channel] = { total: Number(row.total) || 0, closed: Number(row.closed) || 0 };
      }
      setPrevChannelStats(pch);
    }).finally(() => {
      if (mounted) setIsLoading(false);
    });
    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodDays, customFrom, customTo, filterUserId, filterQueueId, filterWhatsappId]);

  // Live KPI refresh: subscribe to ticket socket events so liveOpen /
  // livePending / liveActive update in real-time without a manual refresh.
  // Only counters + attendants are refreshed — chart data is period-window
  // and doesn't change on a single ticket status update (avoids chart jitter).
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;
    const socket = socketManager.GetSocket(companyId);

    const onTicketChange = () => {
      // HIGH (Amit review 2026-06-08): custom date range — don't auto-refresh.
      // An empty find({}) returns today-only data and would silently overwrite
      // the user's custom-range counters/attendants without any UI signal.
      if (periodDays === "custom") return;
      const now = Date.now();
      if (now - liveRefreshThrottleRef.current < 5000) return; // 5s throttle
      liveRefreshThrottleRef.current = now;
      const params = {};
      if (periodDays !== "custom" && periodDays) params.days = periodDays;
      // MEDIUM (Amit review 2026-06-08): mirror entity filters so the
      // refreshed attendants table stays scoped to whatever filter the
      // user has active (queue / agent / WhatsApp). Without this, the
      // 5s socket refresh briefly swaps the filtered table for the
      // unfiltered one.
      if (filterUserId)     params.userId     = filterUserId;
      if (filterQueueId)    params.queueId    = filterQueueId;
      if (filterWhatsappId) params.whatsappId = filterWhatsappId;
      find(params).then((data) => {
        const root = Array.isArray(data) ? data[0] : data;
        if (!root) return;
        setCounters(root.counters || {});
        setAttendants(Array.isArray(root.attendants) ? root.attendants : []);
      });
    };

    socket.on(`company-${companyId}-ticket`, onTicketChange);
    return () => {
      socket.off(`company-${companyId}-ticket`, onTicketChange);
    };
  // Filter state added to deps so the onTicketChange closure picks up the
  // current values. Re-subscribing on filter change is cheap (single
  // socket.off + socket.on) — cheaper than a refs-mirror dance, and keeps
  // the handler signature simple.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodDays, socketManager, filterUserId, filterQueueId, filterWhatsappId]);

  const greetingName = (user?.name || "").split(" ").filter(Boolean).pop() || "אור";
  const dateRange = useMemo(() => {
    if (periodDays === "custom") {
      if (customFrom && customTo) {
        const fmt = (s) => s.split("-").reverse().slice(0, 2).join("/");
        return `${fmt(customFrom)} - ${fmt(customTo)}`;
      }
      return T("range.custom", "מותאם אישית");
    }
    return formatRange(periodDays);
  }, [periodDays, customFrom, customTo]);
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const rangeOptions = useRangeOptions();
  const periodLabel = useMemo(() => {
    if (periodDays === "custom") return T("range.custom", "מותאם אישית");
    return rangeOptions.find(o => o.value === periodDays)?.label || `${periodDays}`;
  }, [periodDays, customFrom, customTo, rangeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active filter count — shown as badge on the filters button
  const activeFilterCount = [filterUserId, filterQueueId, filterWhatsappId].filter(Boolean).length;

  // For "custom" mode derive the number of days from the date inputs so the
  // chart bucketing stays correct (Array.from({ length: "custom" }) = []).
  const effectiveDays = useMemo(() => {
    if (periodDays !== "custom") return periodDays;
    if (customFrom && customTo) {
      const diff = Math.round(
        (new Date(customTo) - new Date(customFrom)) / (24 * 60 * 60 * 1000)
      ) + 1;
      return Math.max(1, diff);
    }
    return 30; // fallback while user fills in dates
  }, [periodDays, customFrom, customTo]);

  // Derive hourly (day=1) or daily buckets from messages.
  // Backend scopes messages to the full window, so for multi-day ranges
  // we bucket by calendar day instead of by hour.
  const messagesChartData = useMemo(
    () => bucketMessagesByPeriod(messages, effectiveDays),
    [messages, effectiveDays]
  );
  const messagesChartTitle = effectiveDays === 1
    ? T("panel.messagesToday", "הודעות שהתקבלו היום")
    : T("panel.messagesRange", `הודעות ב-${periodLabel}`);
  // Tickets array follows the `days` window from the global range
  // picker; the per-chart pill further narrows to today/yesterday/7d
  // /30d for the hourly view.
  const hourlyTickets = useMemo(
    () => bucketByHour(tickets.filter(tk => chartRangeFilter(chartRange)(tk?.createdAt))),
    [tickets, chartRange]
  );

  // 7-day trend sparklines for KPI cards. Computed once over the
  // tickets/messages arrays the API returns so each card shows
  // shape-of-the-week, not just a static number — premium SaaS pattern.
  const messagesSpark = useMemo(() => bucketByDay(messages, 7), [messages]);
  const ticketsSpark = useMemo(() => bucketByDay(tickets, 7), [tickets]);

  // Each KPI is wired to the actual field the /dashboard endpoint
  // returns. Mapping (backend → label):
  //   messages.length      → "New messages"   (count of msgs in period)
  //   supportHappening     → "Open"           (status='open')
  //   supportPending       → "Pending"        (status='pending')
  //   supportClosed        → "Closed"         (status='closed')
  //   totalTickets         → "Total tickets"  (all tickets in period)
  //   leads                → "Unique customers" (distinct contactIds)
  // Old layout double-counted: "Open" and "In Progress" both showed
  // supportHappening (system has only 3 ticket statuses so there is
  // no separate in-progress), and "New messages" was actually the
  // totalTickets number. Fixed.
  // useMemo: previously a fresh array/object literal every render, which
  // defeated the KpiCard React.memo above — the throttled 5s live-socket
  // tick (onTicketChange) re-renders Dashboard on every counters/attendants
  // update, and without this the 6 KpiCards always saw new prop identities
  // and re-rendered anyway. Deps mirror what the values below actually read.
  const kpis = useMemo(() => [
    {
      label: T("kpi.messagesReceived", "הודעות שהתקבלו"),
      // messagesReceived is computed server-side from Messages table — exact count,
      // never capped. messages.length is only a fallback (array is LIMIT 5000).
      value: (counters?.messagesReceived ?? messages?.length ?? 0).toLocaleString(),
      color: PALETTE.green,
      icon: ChatBubbleOutlineRoundedIcon,
      spark: messagesSpark,
    },
    {
      label: T("kpi.open", "פתוחים כרגע"),
      // liveOpen has NO date filter — reflects actual current open tickets
      // regardless of the selected period. Prevents the "0 open" paradox
      // when filtering to "today" with tickets opened yesterday.
      value: (counters?.liveOpen ?? 0).toLocaleString(),
      color: PALETTE.orange,
      icon: FolderOpenRoundedIcon,
      spark: ticketsSpark,
    },
    {
      label: T("kpi.pending", "ממתינים כרגע"),
      // Same rationale — livePending is always current, not period-scoped
      value: (counters?.livePending ?? 0).toLocaleString(),
      color: PALETTE.purple,
      icon: HourglassEmptyRoundedIcon,
    },
    {
      label: T("kpi.totalTickets", "סה\"כ פניות"),
      value: (counters?.totalTickets ?? 0).toLocaleString(),
      color: PALETTE.pink,
      icon: AssignmentIndRoundedIcon,
      spark: ticketsSpark,
      trend: calcTrend(counters?.totalTickets, prevCounters?.totalTickets),
    },
    {
      label: T("kpi.closed", "נסגרו"),
      value: (counters?.supportClosed ?? 0).toLocaleString(),
      color: PALETTE.cyan,
      icon: CheckCircleOutlineRoundedIcon,
      spark: ticketsSpark,
      trend: calcTrend(counters?.supportClosed, prevCounters?.supportClosed),
    },
    {
      label: T("kpi.uniqueCustomers", "לקוחות ייחודיים"),
      value: (counters?.leads ?? 0).toLocaleString(),
      color: PALETTE.red,
      icon: PersonAddRoundedIcon,
      trend: calcTrend(counters?.leads, prevCounters?.leads),
    },
  ], [counters, prevCounters, messages, messagesSpark, ticketsSpark]); // eslint-disable-line react-hooks/exhaustive-deps

  // Donut: real counts from `counters`. We don't fabricate an
  // "other" bucket — only the segments the API actually returns.
  // Channel analytics KPI data — computed from the channelStats / prevChannelStats
  // objects returned by the /dashboard endpoint.  Always renders the three
  // supported channels (whatsapp, facebook, instagram) so the card row has
  // consistent width even when one channel has zero activity.
  const channelKpis = useMemo(() => {
    const CHANNELS = ["whatsapp", "facebook", "instagram", "telegram", "email"];
    return CHANNELS.map((ch) => {
      const curr = channelStats[ch]     || { total: 0, closed: 0 };
      const prev = prevChannelStats[ch] || null;
      return {
        channel:   ch,
        total:     curr.total,
        closed:    curr.closed,
        prevTotal: prev?.total ?? null,
        trend:     calcTrend(curr.total, prev?.total ?? undefined),
      };
    });
  }, [channelStats, prevChannelStats]);

  // Donut palette tuned to the minimal reference style — green
  // for the "active/positive" segment (open), warm neutral for
  // pending, dark slate for closed. Drops the WhatsApp gradient
  // palette in favour of one accent + two muted tones.
  const donutData = useMemo(() => {
    const rows = [
      { name: T("status.open", "פתוח"),    value: counters?.supportHappeningPeriod ?? counters?.supportHappening ?? 0, color: "#22c55e" },
      { name: T("status.pending", "ממתין"), value: counters?.supportPendingPeriod   ?? counters?.supportPending   ?? 0, color: "#d4d4d8" },
      { name: T("status.closed", "סגור"),   value: counters?.supportClosed || 0,    color: "#0B0D0C" },
    ].filter(r => r.value > 0);
    const total = rows.reduce((s, r) => s + r.value, 0);
    return rows.map(r => ({
      ...r,
      pct: total > 0 ? Math.round((r.value / total) * 100) : 0,
    }));
  }, [counters]);
  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  // Real metrics only — anything we don't compute server-side is
  // dropped rather than shown with a fake number.
  const generalMetrics = useMemo(() => [
    { label: T("metric.avgHandle",    "זמן טיפול ממוצע"),  value: formatDuration(counters?.avgSupportTime) },
    { label: T("metric.avgWait",      "זמן מענה ממוצע"),   value: formatDuration(counters?.avgWaitTime) },
    { label: T("metric.totalTickets", "סה\"כ פניות"),       value: (counters?.totalTickets ?? 0).toLocaleString() },
    { label: T("metric.uniqueTickets","פניות ייחודיות"),    value: (counters?.uniqueTickets ?? 0).toLocaleString() },
    // messagesSent is already computed server-side (fromMe=true) — shows
    // total outbound messages the team sent in the selected period.
    { label: T("metric.messagesSent", "הודעות שנשלחו"),     value: (counters?.messagesSent ?? 0).toLocaleString() },
  ], [counters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each attendant row is wired directly to the agents' real numbers
  // returned by /dashboard. Backend surfaces avgSupportTime, avgWaitTime,
  // openCount, closeCount, overallCount — all shown in the expanded table.
  const attendantRows = useMemo(() => {
    const rows = (attendants || []).map(a => {
      const overall = Number(a?.overallCount) || 0;
      const closed = Number(a?.closeCount) || 0;
      const closeRate = overall > 0 ? Math.round((closed / overall) * 100) : null;
      return {
        name: a?.name || "—",
        status: a?.online ? "online" : "offline",
        openCount: Number(a?.openCount) || 0,
        tickets: Number(a?.tickets) || Number(a?.overallCount) || 0,
        avgSupport: formatDuration(a?.avgSupportTime),
        avgWait: formatDuration(a?.avgWaitTime),
        rating: closeRate != null ? `${closeRate}%` : "—",
        // Conversation Starts — Raviv 2026-06-08. Additive read-only field
        // from /dashboard. Falls back to a zero shape if backend omitted it
        // (older deployment) so the column still renders cleanly.
        conversationStarts: a?.conversationStarts || {
          total: 0,
          newTickets: 0,
          reactivations: 0,
        },
      };
    });
    // Totals row
    if (rows.length > 0) {
      const totalTickets = rows.reduce((s, r) => s + r.tickets, 0);
      const totalOpen = rows.reduce((s, r) => s + r.openCount, 0);
      // Conversation Starts totals — sum across agents only. newTickets /
      // reactivations totals are also summed so a downstream reader sees a
      // self-consistent shape (not misleading 0s).
      const totalCs = rows.reduce(
        (s, r) => s + (r.conversationStarts?.total || 0),
        0
      );
      const totalNewTickets = rows.reduce(
        (s, r) => s + (r.conversationStarts?.newTickets || 0),
        0
      );
      const totalReactivations = rows.reduce(
        (s, r) => s + (r.conversationStarts?.reactivations || 0),
        0
      );
      rows.push({
        name: T("table.total", 'סה"כ'),
        status: "__total__",
        openCount: totalOpen,
        tickets: totalTickets,
        avgSupport: "—",
        avgWait: "—",
        rating: "—",
        conversationStarts: {
          total: totalCs,
          newTickets: totalNewTickets,
          reactivations: totalReactivations,
        },
      });
    }
    return rows;
    // i18n.language so the totals row label re-evaluates on language switch.
  }, [attendants, i18n.language]);

  // Dark pill tooltip — Greyola/fintech-reference style: solid
  // near-black background, white label/value, no border, soft
  // shadow, slightly narrower than default Recharts tooltip.
  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div
        style={{
          background: "#0B0D0C",
          color: "#fafafa",
          borderRadius: 12,
          padding: "10px 14px",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: -0.2,
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.28)",
          fontFeatureSettings: "'tnum' 1",
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.6, marginBottom: 2 }}>
          {label}
        </div>
        <div>{payload[0].value} {T("unit.messages", "הודעות")}</div>
      </div>
    );
  };

  return (
    <div className={classes.page}>
      {isLoading && (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={40} style={{ color: "#128c7e" }} />
        </div>
      )}
      <div className={classes.headerRow}>
        <div>
          <div className={classes.greeting}>
            {greeting}, {greetingName}
            <Chip
              label={
                user?.profile === "admin" ? T("role.admin", "מנהל") :
                user?.profile === "supervisor" ? T("role.supervisor", "מפקח") :
                T("role.agent", "נציג")
              }
              size="small"
              className={classes.roleBadge}
              style={{
                background:
                  user?.profile === "admin" ? "#16a34a" :
                  user?.profile === "supervisor" ? "#0ea5e9" :
                  "#6366f1",
                color: "#fff",
              }}
            />
            <span>{T("subtitle", "הינה תמונת מצב חיה של הצוות שלך")}</span>
          </div>
          {/* Live system pulse — only chips backed by real numbers.
              The old "system stable" green dot was hardcoded — it
              would have stayed green even if the backend was on fire,
              so it was misleading. Dropped. */}
          <div className={classes.livePulse}>
            {/* Pulsing live-active chip — shown only when there are active conversations */}
            {(counters?.liveActive ?? 0) > 0 && (
              <Chip
                label={`● ${(counters.liveActive).toLocaleString()} ${T("pulse.activeNow", "שיחות פעילות כרגע")}`}
                size="small"
                className={classes.liveChip}
              />
            )}
            <span className={classes.pulseChip}>
              <strong>{(counters?.liveActive ?? 0).toLocaleString()}</strong>
              <span className="lbl">{T("pulse.activeChats", "שיחות פעילות")}</span>
            </span>
            <span className={classes.pulseChip}>
              <strong>{attendants.filter(a => a?.online).length}</strong>
              <span className="lbl">{T("pulse.onlineAgents", "נציגים מחוברים")}</span>
            </span>
          </div>
        </div>
        <div className={classes.dateGroup} style={{ flexWrap: "wrap" }}>
          {/* Custom date inputs — shown only when "custom" mode is active */}
          {periodDays === "custom" && (
            <div className={classes.customDateRow}>
              <TextField
                type="date"
                label={T("filter.from", "מ-")}
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ minWidth: 140 }}
              />
              <TextField
                type="date"
                label={T("filter.to", "עד")}
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ minWidth: 140 }}
              />
            </div>
          )}
          <div
            className={classes.datePill}
            onClick={(e) => setRangeMenuAnchor(e.currentTarget)}
            role="button"
            tabIndex={0}
          >
            <DateRangeRoundedIcon />
            {dateRange}
          </div>
          <div
            className={classes.dropdownPill}
            onClick={(e) => setRangeMenuAnchor(e.currentTarget)}
            role="button"
            tabIndex={0}
          >
            {/* bdi isolate: when the active locale resolves the label to
                LTR text ("7 days") and the surrounding container is RTL
                (Hebrew page direction), the bidi algorithm renders the
                LTR run as "days 7" — digits and Latin words get reordered
                across the RTL paragraph boundary. <bdi> tells the engine
                to treat this span as its own paragraph for bidi, so the
                label keeps its natural reading order regardless of the
                ancestor direction. */}
            <bdi>{periodLabel}</bdi>
            <ArrowDropDownRoundedIcon />
          </div>
          {/* Filters pill — shows active count badge */}
          <div
            className={`${classes.filterPill} ${activeFilterCount > 0 ? classes.filterPillActive : ""}`}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            role="button"
            tabIndex={0}
          >
            <TuneRoundedIcon />
            {T("filter.label", "פילטרים")}
            {activeFilterCount > 0 && (
              <span style={{
                background: "#128c7e", color: "#fff",
                borderRadius: 999, fontSize: 10, fontWeight: 800,
                padding: "1px 6px", lineHeight: 1.6,
              }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          <Menu
            anchorEl={rangeMenuAnchor}
            open={Boolean(rangeMenuAnchor)}
            onClose={() => setRangeMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {rangeOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                selected={opt.value === periodDays}
                onClick={() => {
                  setPeriodDays(opt.value);
                  setRangeMenuAnchor(null);
                }}
                style={{ minWidth: 160, display: "flex", justifyContent: "space-between", gap: 12 }}
              >
                <span>{opt.label}</span>
                {opt.value === periodDays && (
                  <CheckRoundedIcon style={{ fontSize: 18, color: "#128c7e" }} />
                )}
              </MenuItem>
            ))}
            <MenuItem
              selected={periodDays === "custom"}
              onClick={() => {
                setPeriodDays("custom");
                setRangeMenuAnchor(null);
              }}
              style={{ minWidth: 160, display: "flex", justifyContent: "space-between", gap: 12 }}
            >
              <span>{T("range.custom", "מותאם אישית...")}</span>
              {periodDays === "custom" && (
                <CheckRoundedIcon style={{ fontSize: 18, color: "#128c7e" }} />
              )}
            </MenuItem>
          </Menu>
          {/* Entity filters popover — queue / whatsapp / user.
              PaperProps.className forces width:calc(100vw - 24px) on xs
              per spec C.1 so it never clips off-screen on mobile. */}
          <Popover
            open={Boolean(filterMenuAnchor)}
            anchorEl={filterMenuAnchor}
            onClose={() => setFilterMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ className: classes.filterPopoverPaper }}
          >
            <div className={classes.filterPopover}>
              <div className={classes.filterPopoverTitle}>{T("filter.title", "סינון נתונים")}</div>
              {filterQueues.length > 0 && (
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>{T("filter.queue", "תור")}</InputLabel>
                  <Select
                    value={filterQueueId}
                    onChange={(e) => setFilterQueueId(e.target.value)}
                    label={T("filter.queue", "תור")}
                  >
                    <MenuItem value="">{T("filter.all", "הכל")}</MenuItem>
                    {filterQueues.map((q) => (
                      <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {filterWhatsapps.length > 0 && (
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>{T("filter.whatsapp", "חשבון WhatsApp")}</InputLabel>
                  <Select
                    value={filterWhatsappId}
                    onChange={(e) => setFilterWhatsappId(e.target.value)}
                    label={T("filter.whatsapp", "חשבון WhatsApp")}
                  >
                    <MenuItem value="">{T("filter.all", "הכל")}</MenuItem>
                    {filterWhatsapps.map((w) => (
                      <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {filterUsers.length > 0 && (
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>{T("filter.agent", "נציג")}</InputLabel>
                  <Select
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    label={T("filter.agent", "נציג")}
                  >
                    <MenuItem value="">{T("filter.all", "הכל")}</MenuItem>
                    {filterUsers.map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(filterQueueId || filterWhatsappId || filterUserId) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFilterQueueId("");
                    setFilterWhatsappId("");
                    setFilterUserId("");
                  }}
                  style={{ alignSelf: "flex-start", borderColor: "#e4e4e7", color: "#71717a", fontSize: 12 }}
                >
                  {T("filter.clear", "נקה פילטרים")}
                </Button>
              )}
            </div>
          </Popover>
          {/* Per-chart range menu — shared by both trend panels.
              Same option set, separate state. Hooked to chartMenuAnchor
              and chartRange. */}
          <Menu
            anchorEl={chartMenuAnchor}
            open={Boolean(chartMenuAnchor)}
            onClose={() => setChartMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {[
              { value: "today",     label: T("range.today",     "היום") },
              { value: "yesterday", label: T("range.yesterday", "אתמול") },
              { value: "7d",        label: T("range.7d",        "7 ימים") },
              { value: "30d",       label: T("range.30d",       "30 ימים") },
            ].map((opt) => (
              <MenuItem
                key={opt.value}
                selected={chartRange === opt.value}
                onClick={() => {
                  setChartRange(opt.value);
                  setChartMenuAnchor(null);
                }}
                style={{ minWidth: 160, display: "flex", justifyContent: "space-between", gap: 24 }}
              >
                <span>{opt.label}</span>
                {chartRange === opt.value && (
                  <CheckRoundedIcon style={{ fontSize: 18, color: "#128c7e" }} />
                )}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>

      <Grid container spacing={2}>
        {kpis.map((k) => (
          <Grid key={k.label} item xs={6} sm={4} md={2}>
            <KpiCard classes={classes} {...k} />
          </Grid>
        ))}
      </Grid>

      {/* ─── Channel Source Analytics ─────────────────────────────────── */}
      {/* Always shown so agents see the row even before any channel data  */}
      {/* arrives — cards render 0 / 0% for channels with no activity.    */}
      <div style={{ marginTop: 20, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <span className={classes.channelSectionTitle}>
          {T("channel.sectionTitle", "מקורות שיחה")}
        </span>
        <span style={{ flex: 1, height: 1, background: "#f4f4f5" }} />
      </div>
      {/* Flexbox row — all 5 channels in a single line on desktop,
          wraps gracefully on tablet/mobile. flex: '1 1 0' ensures
          equal-width cards regardless of label length. */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        {channelKpis.map((ck) => (
          <div key={ck.channel} style={{ flex: "1 1 0", minWidth: 150 }}>
            <ChannelKpiCard classes={classes} {...ck} />
          </div>
        ))}
      </div>

      <Grid container spacing={2} className={classes.sectionGrid} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={7}>
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <span className={classes.panelTitle}>{messagesChartTitle}</span>
            </div>
            <div className={classes.chartWrap}>
              <ResponsiveContainer>
                <AreaChart data={messagesChartData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="msgFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="label"
                    // Today (effectiveDays===1) shows 24 hourly labels — force
                    // interval=3 so we render only 8 ticks (00/03/06/.../21).
                    // Longer periods use the dynamic ~8-label formula.
                    interval={effectiveDays === 1 ? 3 : Math.max(0, Math.ceil(effectiveDays / 8) - 1)}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  />
                  <Tooltip
                    content={renderTooltip}
                    cursor={{ stroke: "#0B0D0C", strokeWidth: 1, strokeDasharray: "3 3" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#22c55e"
                    strokeWidth={2.5}
                    fill="url(#msgFill)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#22c55e", stroke: "#fff", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={5}>
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <span className={classes.panelTitle}>{T("panel.ticketsByStatus", "חלוקת כרטיסים לפי סטטוס")}</span>
              {/* Clarify that the donut reflects the selected period — distinct from
                  the "כרגע" live counts shown in the KPI cards above. */}
              <span style={{ fontSize: 11, color: "#a1a1aa", fontWeight: 400, marginRight: 8 }}>
                {T("panel.inPeriod", "בתקופה הנבחרת")}
              </span>
            </div>
            <div className={classes.donutWrap}>
              <div className={classes.donutCenter}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      innerRadius={68}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {donutData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className={classes.donutCenterText}>
                  <span className="num">{donutTotal.toLocaleString()}</span>
                  <span className="lbl">{T("donut.total", 'סה"כ כרטיסים')}</span>
                </div>
              </div>
              <div className={classes.donutLegend}>
                {donutData.map((d) => (
                  <div key={d.name} className={classes.legendRow}>
                    <span className={classes.legendDotName}>
                      <span className={classes.legendDot} style={{ background: d.color }} />
                      {d.name}
                    </span>
                    <span className={classes.legendValue}>
                      {d.value} ({d.pct}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} md={7}>
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <span className={classes.panelTitle}>{T("panel.ticketsByHour", "כרטיסים לפי שעה")}</span>
              <div
                className={classes.dropdownPill}
                style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer" }}
                onClick={(e) => setChartMenuAnchor(e.currentTarget)}
                role="button"
                tabIndex={0}
              >
                {T(`range.${chartRange}`,
                  chartRange === "today" ? "היום"
                  : chartRange === "yesterday" ? "אתמול"
                  : chartRange === "7d" ? "7 ימים"
                  : "30 ימים")}
                <ArrowDropDownRoundedIcon />
              </div>
            </div>
            <div className={classes.chartWrap}>
              <ResponsiveContainer>
                <BarChart data={hourlyTickets} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                  <XAxis
                    dataKey="label"
                    interval={1}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#a1a1aa" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(10, 10, 10, 0.04)" }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <div style={{
                          background: "#0B0D0C", color: "#fafafa",
                          borderRadius: 12, padding: "10px 14px",
                          fontSize: 13, fontWeight: 700, letterSpacing: -0.2,
                          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.28)",
                          fontFeatureSettings: "'tnum' 1",
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.6, marginBottom: 2 }}>
                            {label}
                          </div>
                          <div>{payload[0].value} {T("unit.tickets", "כרטיסים")}</div>
                        </div>
                      );
                    }}
                  />
                  {/* Greyola pattern: most bars in a quiet neutral
                      gray, one bar (the current hour) highlighted
                      in brand green. Lets the eye find "now" at a
                      glance without a noisy gradient everywhere. */}
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {hourlyTickets.map((_, i) => (
                      <Cell
                        key={i}
                        fill={i === new Date().getHours() ? "#22c55e" : "#e4e4e7"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={5}>
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <span className={classes.panelTitle}>{T("panel.generalStats", "סטטיסטיקות כלליות")}</span>
            </div>
            <div className={classes.metricGrid}>
              {generalMetrics.map((m) => (
                <div key={m.label} className={classes.metricCard}>
                  <div className={classes.metricLabel}>{m.label}</div>
                  <div className={classes.metricValue}>{m.value}</div>
                </div>
              ))}
            </div>
          </div>
        </Grid>
      </Grid>

      {sections.length > 0 && (
        <div className={classes.panel} style={{ marginTop: 16, padding: 0, height: "auto" }}>
          <div className={classes.panelHeader} style={{ padding: "18px 20px 0" }}>
            <span className={classes.panelTitle}>
              {T("panel.sections", "כרטיסים לפי טאב מותאם")}
            </span>
          </div>
          {/* Horizontal scroll on phones — 5-col table needs ~560 px and
              would otherwise clip past the panel's right edge on a 360
              px viewport. */}
          <div className={classes.tableScroll}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>{T("table.section", "טאב")}</th>
                  <th className={classes.th}>{T("kpi.open", "פתוחים")}</th>
                  <th className={classes.th}>{T("kpi.pending", "ממתינים")}</th>
                  <th className={classes.th}>{T("kpi.closed", "נסגרו")}</th>
                  <th className={classes.th}>{T("table.total", 'סה"כ')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.id}>
                    <td className={classes.td}>
                      <span className={classes.sectionName}>
                        <span
                          className={classes.sectionSwatch}
                          style={{ background: s.color || "#a1a1aa" }}
                        />
                        {s.name}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <span
                        className={`${classes.sectionStatusBadge} ${classes.badgeOpen}`}
                      >
                        {(s.open ?? 0).toLocaleString()}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <span
                        className={`${classes.sectionStatusBadge} ${classes.badgePending}`}
                      >
                        {(s.pending ?? 0).toLocaleString()}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <span
                        className={`${classes.sectionStatusBadge} ${classes.badgeClosed}`}
                      >
                        {(s.closed ?? 0).toLocaleString()}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <span className={classes.sectionTotal}>
                        {(s.total ?? 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={classes.panel} style={{ marginTop: 16, padding: 0, height: "auto" }}>
        <div className={classes.panelHeader} style={{ padding: "18px 20px 0" }}>
          <span className={classes.panelTitle}>{T("panel.agents", "נציגי שירות")}</span>
        </div>
        {attendantRows.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            {T("agents.empty", "אין נתוני נציגים להצגה")}
          </div>
        ) : (
          // Horizontal scroll on phones — 7-col table is the widest
          // piece of the dashboard. Same wrapper pattern as the
          // sections table above.
          <div className={classes.tableScroll}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>{T("table.status", "סטטוס")}</th>
                  <th className={classes.th}>{T("table.name", "שם")}</th>
                  <th className={classes.th}>{T("table.openCount", "פתוחים")}</th>
                  <th className={classes.th}>{T("table.tickets", "סה\"כ פניות")}</th>
                  <th className={classes.th}>{T("table.avgHandle", "זמן טיפול")}</th>
                  <th className={classes.th}>{T("table.avgWait", "זמן המתנה")}</th>
                  <th className={classes.th}>{T("table.closeRate", "אחוז סגירה")}</th>
                  {/* Conversation Starts — Raviv 2026-06-08. Last column,
                      click on the number opens the modal with the split. */}
                  <th className={classes.th}>{T("table.convStarts", "התחלות שיחה")}</th>
                </tr>
              </thead>
              <tbody>
                {attendantRows.map((r, i) => {
                  const isTotal = r.status === "__total__";
                  return (
                    <tr key={`${r.name}-${i}`} className={isTotal ? classes.totalRow : ""}>
                      <td className={classes.td}>
                        {isTotal ? null : (
                          <Chip
                            label={r.status === "online" ? T("status.online", "מחובר") : T("status.offline", "לא מחובר")}
                            className={`${classes.statusChip} ${r.status === "online" ? classes.statusOnline : classes.statusOffline}`}
                          />
                        )}
                      </td>
                      <td className={classes.td}>
                        <span className={classes.agentNameCell}>
                          {!isTotal && (
                            <span className={classes.agentAvatar}>
                              {(r.name || "?")[0]}
                            </span>
                          )}
                          {r.name}
                        </span>
                      </td>
                      <td className={classes.td}>{r.openCount}</td>
                      <td className={classes.td}>{r.tickets}</td>
                      <td className={classes.td}>{r.avgSupport}</td>
                      <td className={classes.td}>{r.avgWait}</td>
                      <td className={classes.td}>{r.rating}</td>
                      <td className={classes.td}>
                        {isTotal ? (
                          // Total row — sum already pre-computed in useMemo,
                          // not clickable (modal is per-agent only).
                          <span style={{ fontWeight: 700 }}>
                            {r.conversationStarts?.total || 0}
                          </span>
                        ) : (
                          (() => {
                            const cs = r.conversationStarts || { total: 0 };
                            const clickable = cs.total > 0;
                            return (
                              <span
                                style={{
                                  cursor: clickable ? "pointer" : "default",
                                  color: clickable ? "#059669" : "inherit",
                                  fontWeight: 600,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: 44,
                                  minHeight: 44,
                                }}
                                onClick={() =>
                                  clickable &&
                                  setCsModal({
                                    name: r.name,
                                    total: cs.total,
                                    newTickets: cs.newTickets,
                                    reactivations: cs.reactivations,
                                  })
                                }
                              >
                                {cs.total || 0}
                              </span>
                            );
                          })()
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Conversation Starts breakdown modal — opens when a non-zero count
          in the התחלות שיחה column is clicked. Renders nothing while
          csModal is null. */}
      {csModal && (
        <ConversationStartsModal
          open={Boolean(csModal)}
          onClose={() => setCsModal(null)}
          data={csModal}
        />
      )}
    </div>
  );
};

export default Dashboard;
